import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { admin } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { DollarSign, Copy, Save, RefreshCw, Lock, Unlock } from 'lucide-react';

const AdminPricing = () => {
  usePageTitle('Pricing');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  // Bulk pricing dialog
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkCategory, setBulkCategory] = useState(null);
  const [bulkPrice, setBulkPrice] = useState('');
  const [excludeCustom, setExcludeCustom] = useState(true);
  
  // Individual price editing
  const [editingProduct, setEditingProduct] = useState(null);
  const [editPrice, setEditPrice] = useState('');

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    setLoading(true);
    try {
      const response = await admin.getPricing();
      setCategories(response.data.categories);
    } catch (error) {
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPriceUpdate = async () => {
    if (!bulkPrice || parseFloat(bulkPrice) < 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setSaving(true);
    try {
      const response = await admin.updateCategoryPricing({
        category: bulkCategory.category,
        new_price: parseFloat(bulkPrice),
        exclude_custom_pricing: excludeCustom
      });
      
      toast.success(response.data.message);
      setBulkDialog(false);
      loadPricing();
    } catch (error) {
      toast.error('Failed to update category pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleIndividualPriceUpdate = async (productId) => {
    if (!editPrice || parseFloat(editPrice) < 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setSaving(true);
    try {
      const product = categories
        .flatMap(c => c.products)
        .find(p => p.id === productId);
      
      await admin.updateProductPrice(productId, parseFloat(editPrice), product?.custom_pricing || false);
      toast.success('Price updated');
      setEditingProduct(null);
      loadPricing();
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setSaving(false);
    }
  };

  const toggleCustomPricing = async (productId, currentValue) => {
    try {
      const product = categories
        .flatMap(c => c.products)
        .find(p => p.id === productId);
      
      await admin.updateProductPrice(productId, product.price, !currentValue);
      toast.success(currentValue ? 'Custom pricing disabled' : 'Custom pricing enabled');
      loadPricing();
    } catch (error) {
      toast.error('Failed to update custom pricing flag');
    }
  };

  const openBulkDialog = (category) => {
    setBulkCategory(category);
    setBulkPrice(category.min_price === category.max_price ? category.min_price.toString() : '');
    setExcludeCustom(true);
    setBulkDialog(true);
  };

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 w-1/4 rounded" />
          <div className="h-[400px] bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pricing Management</h1>
          <p className="text-gray-600">Manage product pricing individually or by category</p>
        </div>
        <Button variant="outline" onClick={loadPricing} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Category Pricing:</strong> Update all products in a category at once using the &quot;Apply to All&quot; button.
            Products with <Lock className="w-3 h-3 inline" /> custom pricing will be excluded from bulk updates.
          </div>
        </div>
      </div>

      {/* Category Cards */}
      <div className="space-y-4">
        {categories.map(category => (
          <div key={category.category} className="border rounded-lg overflow-hidden">
            {/* Category Header */}
            <div 
              className="bg-gray-50 p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100"
              onClick={() => setExpandedCategory(
                expandedCategory === category.category ? null : category.category
              )}
            >
              <div className="flex items-center gap-4">
                <h3 className="font-semibold text-gray-900 capitalize">
                  {category.category.replace('-', ' ')}
                </h3>
                <Badge variant="outline">{category.products.length} products</Badge>
                <div className="text-sm text-gray-600">
                  Price range: {formatCurrency(category.min_price)} - {formatCurrency(category.max_price)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    openBulkDialog(category);
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Apply to All
                </Button>
                <span className="text-gray-400">
                  {expandedCategory === category.category ? '▼' : '▶'}
                </span>
              </div>
            </div>

            {/* Expanded Products */}
            {expandedCategory === category.category && (
              <div className="divide-y divide-gray-200">
                {category.products.map(product => (
                  <div key={product.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-500 font-mono">{product.sku}</p>
                      </div>
                      {product.custom_pricing && (
                        <Badge className="bg-purple-100 text-purple-700">
                          <Lock className="w-3 h-3 mr-1" />
                          Custom Price
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {editingProduct === product.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            className="w-24 h-9"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => handleIndividualPriceUpdate(product.id)}
                            disabled={saving}
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingProduct(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="text-xl font-bold text-[#002FA7]">
                            {formatCurrency(product.price)}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingProduct(product.id);
                              setEditPrice(product.price.toString());
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleCustomPricing(product.id, product.custom_pricing)}
                            title={product.custom_pricing ? 'Remove custom pricing protection' : 'Protect from bulk updates'}
                          >
                            {product.custom_pricing ? (
                              <Unlock className="w-4 h-4 text-purple-600" />
                            ) : (
                              <Lock className="w-4 h-4 text-gray-400" />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bulk Pricing Dialog */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Category Pricing</DialogTitle>
            <DialogDescription>
              Set a new price for all {bulkCategory?.category.replace('-', ' ')} products
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Category</Label>
              <p className="text-lg font-semibold capitalize mt-1">
                {bulkCategory?.category.replace('-', ' ')}
              </p>
              <p className="text-sm text-gray-500">
                {bulkCategory?.products.length} products • Current range: {bulkCategory && formatCurrency(bulkCategory.min_price)} - {bulkCategory && formatCurrency(bulkCategory.max_price)}
              </p>
            </div>
            
            <div>
              <Label htmlFor="bulk-price">New Price (per unit)</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-gray-500">$</span>
                <Input
                  id="bulk-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-32"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="exclude-custom"
                checked={excludeCustom}
                onCheckedChange={setExcludeCustom}
              />
              <Label htmlFor="exclude-custom" className="text-sm cursor-pointer">
                Skip products with custom pricing
              </Label>
            </div>
            
            {!excludeCustom && (
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm text-yellow-800">
                <strong>Warning:</strong> This will override all prices in this category, including products marked with custom pricing.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkPriceUpdate} 
              disabled={saving || !bulkPrice}
              className="bg-[#002FA7]"
            >
              {saving ? 'Updating...' : `Update ${bulkCategory?.products.filter(p => excludeCustom ? !p.custom_pricing : true).length} Products`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPricing;
